import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { CurveMark } from "./CarparkCurve";

const links = [
  { to: "/about", label: "About" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/services", label: "Services" },
  { to: "/ai-solutions", label: "AI Solutions" },
  { to: "/for-recruiters", label: "Recruiters" },
  { to: "/contact", label: "Contact" },
];

// Batch 2 §10 & §10A — Speaking, Community, Guestbook unpublished.
// The routes still resolve for direct visits (noindex + returnable when live),
// but they're removed from public navigation. moreLinks retained (empty) so
// re-enabling later is a one-line change.
const moreLinks = [];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const { pathname } = useLocation();

  // Pages that render on a cream/light background — the transparent-hero nav
  // treatment would make the cream nav text unreadable, so we force the dark
  // chrome from the top on these routes.
  const isLightBgPage = ["/case-studies", "/services", "/brand-kit"].some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
  const showChrome = scrolled || open || moreOpen || isLightBgPage;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMoreOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!moreOpen) return;
    const onClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setMoreOpen(false); };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  return (
    <>
      <a href="#main" className="skip-link">Skip to content</a>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          showChrome
            ? "bg-[#0B0B0C]/90 backdrop-blur-md border-b border-[#343437] shadow-[0_10px_30px_-20px_rgba(0,0,0,0.9)]"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 lg:px-10 h-[72px]">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 text-[#FAF9F6] hover:text-[#D8A936] transition-colors">
            <CurveMark className="w-[52px] h-[30px] text-[#FAF9F6]" />
            <span className="font-display font-extrabold text-[15px] tracking-[0.2em] uppercase">JJ Argent</span>
          </Link>

          <nav aria-label="Primary" className="hidden lg:flex items-center gap-6">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `nav-item font-display text-[11.5px] tracking-[0.14em] uppercase transition-colors whitespace-nowrap ${
                    isActive ? "nav-item--active text-[#D8A936]" : "text-[#FAF9F6]/85 hover:text-[#D8A936]"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            {moreLinks.length > 0 && (
              <div className="relative" ref={moreRef}>
                <button
                  type="button"
                  data-testid="nav-more"
                  aria-haspopup="menu"
                  aria-expanded={moreOpen}
                  onClick={() => setMoreOpen((v) => !v)}
                  className="nav-item font-display text-[11.5px] tracking-[0.14em] uppercase text-[#FAF9F6]/85 hover:text-[#D8A936] transition-colors inline-flex items-center gap-1"
                >
                  More <ChevronDown size={12} className={`transition-transform ${moreOpen ? "rotate-180" : ""}`} />
                </button>
                {moreOpen && (
                  <div
                    role="menu"
                    data-testid="nav-more-menu"
                    className="absolute right-0 mt-3 min-w-[180px] border border-[#343437] bg-[#0B0B0C] shadow-[0_20px_40px_-20px_rgba(0,0,0,0.9)]"
                  >
                    {moreLinks.map((l) => (
                      <NavLink
                        key={l.to}
                        to={l.to}
                        role="menuitem"
                        data-testid={`nav-more-${l.label.toLowerCase()}`}
                        className={({ isActive }) =>
                          `block px-4 py-3 font-display text-[11.5px] tracking-[0.14em] uppercase border-b border-[#343437] last:border-b-0 ${
                            isActive ? "text-[#D8A936]" : "text-[#FAF9F6]/85 hover:text-[#D8A936] hover:bg-[#151517]"
                          }`
                        }
                        onClick={() => setMoreOpen(false)}
                      >
                        {l.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            )}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/contact" data-testid="nav-work-together" className="hidden md:inline-flex btn-primary !py-2.5 !px-4 !text-[11px]">
              Work Together
            </Link>
            <button
              type="button"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden w-11 h-11 flex items-center justify-center border border-[#343437] text-[#FAF9F6] hover:text-[#D8A936] hover:border-[#D8A936] transition-colors"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {/* Mobile sheet */}
        {open && (
          <div className="lg:hidden border-t border-[#343437] bg-[#0B0B0C]">
            <nav aria-label="Mobile" className="px-6 py-8 flex flex-col gap-5">
              {[...links, ...moreLinks].map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `font-display text-2xl tracking-tight ${isActive ? "text-[#D8A936]" : "text-[#FAF9F6]"}`
                  }
                  end={l.to === "/"}
                >
                  {l.label}
                </NavLink>
              ))}
              <Link to="/contact" data-testid="mobile-nav-work-together" className="btn-primary mt-3 self-start">
                Start a conversation
              </Link>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
